import { EDepositStatus } from "src/types";
import { getApiUrl } from "src/utils";
import {
  CxmCreatePayoutAccountPayload,
  CxmDirection,
  CxmInitiateDepositPayload,
  CxmInitiateDepositResponse,
  CxmMethod,
  CxmPayoutAccount,
  CxmPayoutAccountRequirements,
  CxmQuote,
  CxmQuotePreviewPayload,
  CxmSupportedOptionsResponse,
} from "./types";

export enum CxmEndpoints {
  SUPPORTED_OPTIONS = "/psp/cxm/supported-options",
  QUOTE_PREVIEW = "/psp/cxm/quote-preview",
  PAYOUT_ACCOUNTS = "/psp/cxm/payout-accounts",
  PAYOUT_ACCOUNT_REQUIREMENTS = "/psp/cxm/payout-accounts/requirements",
  INITIATE_WITHDRAWAL = "/psp/cxm/initiate-withdrawal",
  INITIATE_DEPOSIT = "/psp/cxm/initiate-deposit",
}

/**
 * The route's PSP is not exposed to the iframe, so the provider is chosen by
 * the tenant the widget was opened for. Matches `CXM`, `CXM-UAT` and any
 * future `CXM-*` env the same way the backend gates on `psp_service_id`.
 */
export function isCxmExchange(exchange?: string | null): boolean {
  const name = (exchange ?? "").trim().toUpperCase();
  return name === "CXM" || name.startsWith("CXM-");
}

/**
 * Where the hosted checkout should send the customer back to.
 *
 * Both point at the widget's own `/deposit-status/*`, which is what already
 * knows how to escape the frame: it posts the outcome up to the widget window
 * (`useSendDepositStatusRedirect` / `useReceiveDepositStatusRedirect`).
 *
 * Derived from the running origin rather than hardcoded, so every tenant's
 * widget host works without a per-env constant. On a plain-http dev origin
 * both are omitted — the backend rejects anything that is not absolute https,
 * and a rejected deposit is worse than one that ends on the gateway's own
 * confirmation page.
 */
export function cxmDepositReturnUrls(): {
  returnUrl?: string;
  cancelUrl?: string;
} {
  const origin = window.location.origin;
  if (!origin.startsWith("https://")) return {};
  return {
    returnUrl: new URL(`/deposit-status/${EDepositStatus.Success}`, origin).toString(),
    cancelUrl: new URL(`/deposit-status/${EDepositStatus.Cancel}`, origin).toString(),
  };
}

/** The partner gateway's own provider id, distinct from the CXM tenant's. */
export const CXM_DIRECT_PSP_SERVICE_ID = "CXMDIRECT";

/**
 * Whether a payment route is served by the CXM partner gateway.
 *
 * This, not `isCxmExchange`, decides which flow renders. The CXM tenant has 52
 * routes under its own older `CXM` provider, so on that exchange the tenant
 * name says nothing about which provider a given route belongs to. The
 * exchange name is still the right question for theming and for which loader
 * to show while the route is still loading.
 */
export function isCxmDirectRoute(pspServiceId?: string): boolean {
  return pspServiceId === CXM_DIRECT_PSP_SERVICE_ID;
}

/**
 * `max.amount === "0"` means the method has no ceiling. Read literally it
 * caps every payment at zero, which silently disables the method — so limits
 * must always go through here rather than being compared inline.
 */
export function methodLimits(method: CxmMethod): {
  min: number | null;
  max: number | null;
} {
  const min = Number(method.limits?.min?.amount);
  const max = Number(method.limits?.max?.amount);
  return {
    min: Number.isFinite(min) && min > 0 ? min : null,
    max: Number.isFinite(max) && max > 0 ? max : null,
  };
}

/**
 * The gateway's `quote` endpoint is an FX calculator, not a validator — it
 * returns 200 for amounts above the maximum and below the minimum alike, and
 * only `create` refuses them. Validate here so the user is not given a green
 * light followed by a rejection.
 */
export function validateAmount(method: CxmMethod, amount: string): string | null {
  if (!/^\d+(\.\d+)?$/.test(amount.trim())) return "Enter an amount.";
  const value = Number(amount);
  if (!Number.isFinite(value)) return "Enter an amount.";
  // Zero is an amount — it is just not a sendable one. Saying "Enter an
  // amount" to someone who has entered 0 tells them the field is empty when
  // they can see that it is not.
  if (value <= 0) return "Enter an amount greater than 0.";

  const { min, max } = methodLimits(method);
  if (min !== null && value < min) {
    return `The minimum for ${method.displayName} is ${min} ${method.currency}.`;
  }
  if (max !== null && value > max) {
    return `The maximum for ${method.displayName} is ${max} ${method.currency}.`;
  }
  return null;
}

/** Strips the stray trailing bracket in type 17's holder-name label. */
export function fieldLabel(name: string): string {
  return name.replace(/\)\)$/, ")");
}

export function visibleFields(requirements: CxmPayoutAccountRequirements): CxmPayoutAccountField[] {
  return (requirements.fields ?? []).filter((f) => !f.hidden);
}

/**
 * The options a select can currently offer.
 *
 * A flat list stands on its own. An object is keyed by the answer to
 * `dependsOn` — country picks the bank list — so before that parent is
 * answered the field has nothing to offer and must stay closed rather than
 * showing every group merged together.
 */
export function fieldOptions(field: CxmPayoutAccountField, values: Record<string, string>): { value: string; label: string }[] {
  if (!field.options) return [];
  if (Array.isArray(field.options)) return field.options;
  const parent = String(values[field.dependsOn ?? ""] ?? "").trim();
  return field.options[parent] ?? [];
}

/** Fields whose value is dictated by another one, directly or further down. */
export function dependentFields(fields: CxmPayoutAccountField[], name: string): string[] {
  const direct = fields.filter((f) => f.dependsOn === name).map((f) => f.name);
  return direct.flatMap((child) => [child, ...dependentFields(fields, child)]);
}

/**
 * Anything the user has no choice about is answered for them: a select with a
 * single option, which is what `source: "profile"` produces — the gateway
 * dictates it from the user's residence and offers only that one country.
 * Asking someone to pick from a list of one is noise, and leaving it blank
 * fails validation for a value they could not have changed.
 */
export function autoSelectedValues(fields: CxmPayoutAccountField[], values: Record<string, string>): Record<string, string> {
  const filled: Record<string, string> = {};
  for (const field of fields) {
    if (field.kind !== "select") continue;
    if (String(values[field.name] ?? "").trim()) continue;
    const options = fieldOptions(field, values);
    if (options.length === 1) filled[field.name] = options[0].value;
  }
  return filled;
}

/**
 * The gateway validates payout details not at all — it stores `{}` and
 * unknown keys as `approved`, and a wrong key produces an account it cannot
 * read, so the payout later leaves without a beneficiary. The backend
 * enforces this too; doing it here as well keeps the error next to the field
 * instead of arriving as a server rejection.
 */
export function validatePayoutDetails(
  requirements: CxmPayoutAccountRequirements,
  details: Record<string, string>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of visibleFields(requirements)) {
    // Schema v2 says so per field. Before it existed every visible field was
    // treated as mandatory, which would now reject a valid account for
    // leaving an optional field blank.
    const value = String(details?.[field.name] ?? "").trim();

    // A select value outside the schema is accepted by the gateway and then
    // unreadable to it, so the backend refuses it too. Checked before the
    // required rule so an optional select still cannot carry junk.
    if (value && field.kind === "select" && field.options) {
      const options = fieldOptions(field, details);
      if (!options.some((o) => o.value === value)) {
        errors[field.name] = `Choose a ${fieldLabel(field.name)} from the list.`;
        continue;
      }
    }

    if (field.required === false) continue;
    if (!value) {
      errors[field.name] = `${fieldLabel(field.name)} is required.`;
    }
  }
  return errors;
}

type CxmPayoutAccountField = CxmPayoutAccountRequirements["fields"][number];

/**
 * Talks to the vakotrade backend, which signs and proxies to the CXM partner
 * gateway. The widget never holds `cxm_request_secret`, and never sends a
 * user id — the backend takes it from the auth token.
 */
export class CxmService {
  private apiUrl: string;
  private authorization?: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl ?? getApiUrl() ?? "";
  }

  public setToken(token?: string): void {
    if (token) this.authorization = token;
  }

  private async request<T = any>(
    method: "GET" | "POST" | "DELETE",
    endpoint: string,
    options?: {
      body?: Record<string, any>;
      params?: Record<string, string>;
    }
  ): Promise<T> {
    const queryString = options?.params ? "?" + new URLSearchParams(options.params).toString() : "";

    const response = await fetch(`${this.apiUrl.replace("/graphql", "")}${endpoint}${queryString}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        ...(this.authorization ? { Authorization: this.authorization } : {}),
      },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let detail: string | null = null;
      try {
        const parsed = JSON.parse(errorBody) as {
          message?: string;
          error?: { code?: string; message?: string };
        };
        // The backend forwards the gateway's `{error:{code,message}}` shape
        // for upstream failures, and its own `{message}` otherwise.
        detail = parsed?.error?.message ?? parsed?.message ?? null;
      } catch {
        // not JSON — keep the status line
      }
      console.warn(`CXM request failed: ${response.status} ${response.statusText}. ${errorBody}`);
      throw new Error(detail ?? `HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /**
   * The catalogue is personalised per user and can come back empty when the
   * operator has granted no payment rules — that is a legitimate state, not
   * an error, and the UI should say so rather than spin.
   */
  public async getMethods(direction: CxmDirection): Promise<CxmMethod[]> {
    const res = await this.request<CxmSupportedOptionsResponse>("GET", CxmEndpoints.SUPPORTED_OPTIONS, { params: { direction } });
    return res?.methods ?? [];
  }

  public quotePreview(payload: CxmQuotePreviewPayload): Promise<CxmQuote> {
    return this.request<CxmQuote>("POST", CxmEndpoints.QUOTE_PREVIEW, {
      body: payload,
    });
  }

  public getPayoutAccountRequirements(paymentMethodTypeId: number): Promise<CxmPayoutAccountRequirements> {
    return this.request<CxmPayoutAccountRequirements>("GET", CxmEndpoints.PAYOUT_ACCOUNT_REQUIREMENTS, {
      params: { paymentMethodTypeId: String(paymentMethodTypeId) },
    });
  }

  /** Returns a bare array, not an `{items}` envelope. */
  public async listPayoutAccounts(): Promise<CxmPayoutAccount[]> {
    const res = await this.request<CxmPayoutAccount[]>("GET", CxmEndpoints.PAYOUT_ACCOUNTS);
    return Array.isArray(res) ? res : [];
  }

  public createPayoutAccount(payload: CxmCreatePayoutAccountPayload): Promise<{ id: number }> {
    return this.request<{ id: number }>("POST", CxmEndpoints.PAYOUT_ACCOUNTS, {
      body: payload,
    });
  }

  public deletePayoutAccount(id: number): Promise<unknown> {
    return this.request("DELETE", CxmEndpoints.PAYOUT_ACCOUNTS, {
      body: { id },
    });
  }

  /**
   * Creates the withdrawal payment on our side and stores the CXM context on
   * it. Nothing reaches the gateway until the payment job dispatches, so a
   * successful response means "accepted", not "paid".
   */
  public initiateWithdrawal(payload: {
    payment_route_id: string;
    amount: string;
    methodId: string;
    paymentMethodId: number;
  }): Promise<{ payment_id: string; status: string }> {
    return this.request("POST", CxmEndpoints.INITIATE_WITHDRAWAL, {
      body: payload,
    });
  }

  /**
   * Creates the deposit on our side and asks the gateway how the customer
   * should pay. The answer is in `action`: a hosted checkout to reach, a form
   * to POST, or nothing at all — so the caller must branch on it rather than
   * assume a URL.
   */
  public initiateDeposit(payload: CxmInitiateDepositPayload): Promise<CxmInitiateDepositResponse> {
    return this.request<CxmInitiateDepositResponse>("POST", CxmEndpoints.INITIATE_DEPOSIT, { body: payload });
  }
}

export const cxmService = new CxmService();
